import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react";
import { Button, SegmentedControl, Spinner } from "@playerelite/ui";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { runAuthHandler } from "../util";
import { MapFence, TIME_RANGES } from "./geo-analytics-utils";
import TierPanel from "./TierPanel";
import { useTierCounts, useVenueFences } from "./use-geo-analytics";
import VenueMapCard from "./VenueMapCard";

const RANGE_OPTIONS = TIME_RANGES.map((r) => ({ value: r.key, label: r.label }));

// One instance, so the map's effects do not see a "new" fence list on every render while the
// fences load or fail, and keep snapping the map back to the default view.
const NO_FENCES: MapFence[] = [];

const GeoAnalyticsPage = () => {
  const userStore = useContext(UserStore);
  const navigate = useNavigate();
  const venueId = userStore.selectedVenue?.venueId;
  const [range, setRange] = useState(TIME_RANGES[0]);
  const [selectedFence, setSelectedFence] = useState<string | null>(null);

  useEffect(() => {
    runAuthHandler({ userStore, navigate });
  }, [venueId, userStore, navigate]);

  // Another venue has different fences, so a selection from the last one means nothing.
  useEffect(() => {
    setSelectedFence(null);
  }, [venueId]);

  const fences = useVenueFences(venueId);
  const tiers = useTierCounts(venueId, range);

  return (
    <section className="bg-surface-sunken text-fg">
      <div className="mx-auto flex max-w-screen-xl flex-col gap-6 px-4 py-8 lg:px-6">
        <BreadCrumbs
          routes={[
            { path: "/home", breadcrumbName: "Home" },
            { path: "/geo-analytics", breadcrumbName: "Geofence Analytics" },
          ]}
        />

        <header>
          <h1 className="text-xl font-semibold">Geofence Analytics</h1>
          <p className="text-sm text-fg-muted">
            Monitor member visits across venue geofences by loyalty tier.
          </p>
        </header>

        <VenueMapCard
          fences={fences.data ?? NO_FENCES}
          isLoading={fences.isPending && venueId != null}
          isError={fences.isError}
          onRetry={() => fences.refetch()}
          selected={selectedFence}
          onSelect={setSelectedFence}
        />

        <div className="flex flex-wrap items-center justify-center gap-3">
          <SegmentedControl
            aria-label="Reporting period"
            options={RANGE_OPTIONS}
            value={range.key}
            onValueChange={(key) => {
              const next = TIME_RANGES.find((r) => r.key === key);
              if (next) setRange(next);
            }}
          />
          {tiers.isFetching && tiers.data ? (
            <Spinner size="sm" label="Recalculating counts" />
          ) : null}
        </div>

        {/* A failed refresh keeps the last counts, so say they were not recalculated. */}
        {tiers.isError && tiers.data && !tiers.isFetching ? (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-center gap-3 rounded-md border border-warning/40 bg-warning/10 px-4 py-2 text-sm"
          >
            <span>Couldn't recalculate the counts. Showing the last figures loaded.</span>
            <Button variant="secondary" size="sm" onClick={() => tiers.refetch()}>
              Try again
            </Button>
          </div>
        ) : null}

        {tiers.isPending && venueId != null ? (
          <div className="flex justify-center py-12 text-fg-muted">
            <Spinner size="md" label="Loading tier counts" />
          </div>
        ) : tiers.isError && !tiers.data ? (
          <div className="flex flex-col items-center gap-3 py-12 text-sm text-fg-muted">
            <span>Couldn't load the tier counts.</span>
            <Button variant="secondary" size="sm" onClick={() => tiers.refetch()}>
              Try again
            </Button>
          </div>
        ) : tiers.data && tiers.data.length > 0 ? (
          <section
            aria-label="Members by tier"
            aria-busy={tiers.isFetching}
            className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-4 ${
              tiers.isPlaceholderData ? "opacity-60" : ""
            }`}
          >
            {tiers.data.map((tier) => (
              <TierPanel key={tier.tierNumber} tier={tier} selected={selectedFence} />
            ))}
          </section>
        ) : (
          <p className="py-12 text-center text-sm text-fg-muted">
            No geofence activity for this period.
          </p>
        )}
      </div>
    </section>
  );
};

export default observer(GeoAnalyticsPage);
